<script setup lang="ts">
import { useTime } from '@/composables/useTime';
import { PROGRESS_DASHBOARDS, type ProgressDashboardKind } from '@/utils/constants/progressDashboardConfig';
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useRBCDashboard } from '@/composables/useRBCDashboard';
import { useApi } from '@/composables/useAppGlobals';
import SummaryCard from './SummaryCard.vue';
import ProgressTable from './ProgressTable.vue';

const props = defineProps<{ kind?: ProgressDashboardKind }>();
const kind = computed(() => props.kind ?? 'rbc');
const config = computed(() => PROGRESS_DASHBOARDS[kind.value]);
const { dateTimeFormat } = useTime();
const route = useRoute();
const projectId = computed(() => route.params.id as string);

const { 
  data,
  loading, 
  error, 
  groupedData, 
  overallProgress, 
  completedCount, 
  totalCategories,
  fetchDashboard
} = useRBCDashboard(projectId, kind);

// Watch for project ID changes to trigger a fresh fetch
watch([projectId, kind], () => {
  fetchDashboard();
});

const api = useApi();
const refreshing = ref(false);

const triggerRefresh = async () => {
  if (refreshing.value) return;
  refreshing.value = true;
  const activeProject = projectId.value;
  const activeKind = kind.value;
  try {
    await api.triggerAggregation(activeProject, activeKind);
    if (activeProject === projectId.value && activeKind === kind.value) await fetchDashboard();
  } catch (err) {
    if (activeProject !== projectId.value || activeKind !== kind.value) return;
    error.value = (err as { response?: { data?: { detail?: string } }; message?: string }).response?.data?.detail
      || (err as Error).message || 'Failed to refresh dashboard';
  } finally {
    refreshing.value = false;
  }
};
</script>

<template>
  <div class="container-fluid py-5 bg-dashboard min-vh-100">
    <div class="max-width-1200 mx-auto px-4">
      <!-- Header -->
      <header class="mb-5 d-flex justify-content-between align-items-end">
        <div>
          <h1 class="fw-black display-5 mb-1">
            {{ config.title }}
          </h1>
          <p class="text-muted fw-semibold">
            Target count per cell type: <span class="text-dark">10,000</span>
          </p>
          <p
            v-if="kind === 'lpc' && data?.create_time"
            class="text-muted small mb-0"
          >
            Last updated {{ dateTimeFormat(data.create_time) }}
          </p>
        </div>
        <button 
          class="btn btn-primary rounded-pill px-4 py-2 d-flex align-items-center gap-2 shadow-sm"
          :disabled="refreshing || loading"
          @click="triggerRefresh"
        >
          <i
            class="bi bi-arrow-clockwise"
            :class="{ 'spin': refreshing }"
          />
          {{ refreshing ? 'Refreshing...' : 'Refresh' }}
        </button>
      </header>

      <!-- Loading State -->
      <div
        v-if="loading && !groupedData.length"
        class="text-center py-5"
      >
        <div
          class="spinner-border text-primary"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="alert alert-danger shadow-sm rounded-4 d-flex align-items-center justify-content-between"
        role="alert"
      >
        <div>
          <i class="bi bi-exclamation-triangle-fill me-2" />
          {{ error }}
        </div>
        <button
          v-if="error.includes('404') || error.includes('not found')"
          class="btn btn-outline-danger btn-sm rounded-pill px-3"
          @click="triggerRefresh"
        >
          Generate Report
        </button>
      </div>

      <!-- Content -->
      <template v-else>
        <!-- Summary Stats -->
        <div class="row g-4 mb-5">
          <div class="col-md-6">
            <SummaryCard 
              title="Overall Progress" 
              :value="`${overallProgress}%`" 
            />
          </div>
          <div class="col-md-6">
            <SummaryCard 
              title="Completed Types" 
              :value="completedCount" 
              :subtitle="totalCategories.toString()"
            />
          </div>
        </div>

        <!-- Progress Table -->
        <div class="mb-5">
          <ProgressTable :categories="groupedData" />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.bg-dashboard {
  background-color: #f7fafc;
}
.max-width-1200 {
  max-width: 1200px;
}
.fw-black {
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
